import type { Graph, ItemRow, SuggestionRow } from '@/schema/estate';
import type { IdentifyResult } from '../../shared/identify';
import { resolveCategory } from './categoryService';
import {
  type EnrichmentFields,
  getItem,
  setCategory,
  updateEnrichment,
  updateName,
} from './itemService';
import { addItemTag, itemTagNames } from './tagService';

const TEXT_FIELDS = ['name', 'description', 'materials', 'dimensions', 'condition'] as const;
type TextField = (typeof TEXT_FIELDS)[number];
export type SuggestionField = TextField | 'category' | 'tag';

function isTextField(field: string): field is TextField {
  return (TEXT_FIELDS as readonly string[]).includes(field);
}

const ENRICHMENT_COLUMN = {
  description: 'description',
  materials: 'materials',
  dimensions: 'dimensions',
  condition: 'condition_text',
} as const satisfies Record<Exclude<TextField, 'name'>, keyof EnrichmentFields>;

function liveFor(g: Graph, itemId: string): SuggestionRow[] {
  return g.suggestion.all().filter((s) => s.item_id === itemId && !s.archived);
}

export function suggestionsForItem(g: Graph, itemId: string): SuggestionRow[] {
  return liveFor(g, itemId)
    .filter((s) => s.state === 'pending' || s.state === 'applied')
    .sort((a, b) => a.created_at - b.created_at);
}

const ALWAYS_FILL: readonly SuggestionField[] = ['name', 'description', 'category'];

// Enough verdicts that two or three early clicks can't decide a field.
export const AUTO_FILL_MIN_DECIDED = 10;
// An auto-filled value shows only in the field, so a wrong one goes unnoticed.
export const AUTO_FILL_MIN_ACCEPT_RATE = 0.8;

/**
 * Fields whose suggestions from `model` the user accepts often enough to fill an empty field.
 * An `applied` row is the model's own fill, not a verdict: counting it would feed the rate.
 */
function earnedFields(g: Graph, model: string): Set<string> {
  const tally = new Map<string, { accepted: number; decided: number }>();
  for (const s of g.suggestion.all()) {
    if (s.archived || s.model !== model) continue;
    if (s.state !== 'accepted' && s.state !== 'rejected') continue;
    const t = tally.get(s.field) ?? { accepted: 0, decided: 0 };
    t.decided += 1;
    if (s.state === 'accepted') t.accepted += 1;
    tally.set(s.field, t);
  }
  const earned = new Set<string>();
  for (const [field, t] of tally) {
    if (t.decided >= AUTO_FILL_MIN_DECIDED && t.accepted / t.decided >= AUTO_FILL_MIN_ACCEPT_RATE)
      earned.add(field);
  }
  return earned;
}

/** A tag is never empty: tags add to a list rather than fill a blank, so they always wait. */
function isEmpty(item: ItemRow, field: SuggestionField): boolean {
  if (field === 'category') return !item.category_id;
  if (!isTextField(field)) return false;
  const column = field === 'name' ? 'name' : ENRICHMENT_COLUMN[field];
  return (item[column] ?? '').trim() === '';
}

async function applyValue(g: Graph, item: ItemRow, field: string, value: string): Promise<void> {
  if (field === 'tag') {
    await addItemTag(g, {
      itemId: item.id,
      estateId: item.estate_id ?? '',
      groupId: item.owner_group_id ?? '',
      name: value,
    });
    return;
  }
  if (field === 'category') {
    const categoryId = await resolveCategory(g, {
      estateId: item.estate_id ?? '',
      name: value,
      groupId: item.owner_group_id,
      author: item.created_by,
    });
    await setCategory(g, item.id, categoryId);
    return;
  }
  if (!isTextField(field)) return;
  if (field === 'name') {
    await updateName(g, item.id, value);
    return;
  }
  await updateEnrichment(g, item.id, { [ENRICHMENT_COLUMN[field]]: value });
}

export async function recordSuggestions(
  g: Graph,
  itemId: string,
  result: IdentifyResult,
  photoIds: string[],
): Promise<void> {
  const item = getItem(g, itemId);
  if (!item) return;
  const proposals: Array<{ field: SuggestionField; value: string }> = [];
  for (const field of [...TEXT_FIELDS, 'category'] as const) {
    const value = result[field].trim();
    if (value) proposals.push({ field, value });
  }
  const existingTags = new Set(itemTagNames(g, itemId));
  const tags = new Set(
    result.tags
      .map((t) => t.trim().toLowerCase())
      .filter((t) => t && !t.startsWith('flag:') && !existingTags.has(t)),
  );
  for (const value of tags) proposals.push({ field: 'tag', value });

  const fills = new Set<string>([...ALWAYS_FILL, ...earnedFields(g, result.model)]);
  const pending = liveFor(g, itemId).filter((s) => s.state === 'pending');
  const now = Date.now();
  for (const p of proposals) {
    const stale = pending.filter(
      (s) => s.field === p.field && (p.field !== 'tag' || s.value === p.value),
    );
    for (const s of stale) await g.suggestion.update(s.id, { state: 'superseded' });
    const apply = fills.has(p.field) && isEmpty(item, p.field);
    if (apply) await applyValue(g, item, p.field, p.value);
    await g.suggestion.create({
      id: crypto.randomUUID(),
      owner_group_id: item.owner_group_id,
      item_id: itemId,
      field: p.field,
      value: p.value,
      confidence: result.confidence,
      state: apply ? 'applied' : 'pending',
      model: result.model,
      photo_ids: photoIds,
      created_at: now,
      archived: false,
    });
  }
}

export async function acceptSuggestion(g: Graph, id: string): Promise<void> {
  const s = g.suggestion(id)?.$data;
  if (!s || s.state !== 'pending') return;
  const item = getItem(g, s.item_id ?? '');
  if (!item) return;
  await applyValue(g, item, s.field, s.value);
  await g.suggestion.update(id, { state: 'accepted' });
}

export async function rejectSuggestion(g: Graph, id: string): Promise<void> {
  const s = g.suggestion(id)?.$data;
  if (!s || s.state !== 'pending') return;
  await g.suggestion.update(id, { state: 'rejected' });
}
